// ============================================================================
// faceMesh.js — olah 478 titik landmark MediaPipe jadi metrik nyata.
// ============================================================================
// Semua murni geometri di browser (cepat, tanpa server). Dipakai untuk:
//   - Pre-filter kecepatan : hanya kirim frame wajah frontal & jelas ke server
//   - Deteksi oklusi/kualitas: masker, kacamata, mata tertutup, terlalu kecil/miring
//   - Auto-capture enrollment: tahu pose (depan/kiri/kanan/atas/bawah) sudah pas
//   - Liveness/anti-spoof   : deteksi kedip lewat riwayat Eye Aspect Ratio
//
// Landmark MediaPipe ternormalisasi (x,y dalam 0..1 terhadap frame).
// ============================================================================

// Index landmark kunci (MediaPipe FaceMesh, 478 titik termasuk iris).
const L = {
  noseTip: 1,
  chin: 152,
  forehead: 10,
  faceLeftEdge: 234,    // pipi kiri (sisi wajah)
  faceRightEdge: 454,   // pipi kanan
  // Mata kiri
  leyeOuter: 33, leyeInner: 133, leyeTop: 159, leyeBottom: 145,
  // Mata kanan
  reyeOuter: 263, reyeInner: 362, reyeTop: 386, reyeBottom: 374,
  // Mulut
  mouthLeft: 61, mouthRight: 291, mouthTop: 13, mouthBottom: 14,
};

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const mid  = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const deg = (rad) => (rad * 180) / Math.PI;

// Eye Aspect Ratio: tinggi mata / lebar mata. Mata terbuka ~0.28-0.38, tertutup <0.18.
function eyeAspectRatio(lm, top, bottom, outer, inner) {
  const v = dist(lm[top], lm[bottom]);
  const h = dist(lm[outer], lm[inner]) || 1e-6;
  return v / h;
}

// ── Pose kepala (heuristik dari geometri, cukup untuk gating) ────────────────
function headPose(lm) {
  const nose = lm[L.noseTip];
  const leftEye  = mid(lm[L.leyeOuter], lm[L.leyeInner]);
  const rightEye = mid(lm[L.reyeOuter], lm[L.reyeInner]);

  // Roll: kemiringan garis antar-mata.
  const roll = deg(Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x));

  // Yaw: asimetri jarak hidung ke sisi kiri vs kanan wajah.
  const dL = nose.x - lm[L.faceLeftEdge].x;
  const dR = lm[L.faceRightEdge].x - nose.x;
  const yawNorm = (dR - dL) / ((dR + dL) || 1e-6); // -1..1
  const yaw = clamp(yawNorm * 75, -90, 90);        // perkiraan derajat

  // Pitch: posisi vertikal hidung relatif garis-mata dan dagu.
  const eyeY = (leftEye.y + rightEye.y) / 2;
  const chinY = lm[L.chin].y;
  const span = (chinY - eyeY) || 1e-6;
  const ratio = (nose.y - eyeY) / span;            // ~0.40 saat frontal
  const pitch = clamp((ratio - 0.45) * 160, -90, 90); // + = menunduk, - = mendongak

  return {
    yaw:   Math.round(yaw),
    pitch: Math.round(pitch),
    roll:  Math.round(roll),
  };
}

// Klasifikasi pose untuk auto-capture 5 sudut.
function classifyPose({ yaw, pitch }) {
  if (Math.abs(yaw) < 15 && Math.abs(pitch) < 12) return 'DEPAN';
  if (yaw <= -22) return 'KANAN';   // subjek menengok ke kanan-nya (mirror kamera bisa terbalik)
  if (yaw >= 22)  return 'KIRI';
  if (pitch <= -15) return 'ATAS';
  if (pitch >= 18)  return 'BAWAH';
  return 'MIRING';
}

// ── Oklusi (heuristik gabungan landmark + opsional sampling piksel) ──────────
// MediaPipe selalu mengembalikan 478 titik walau tertutup (ia menebak), jadi
// "titik hilang" bukan sinyal. Sinyal yang andal: mata tertutup (EAR) untuk mata,
// dan keseragaman warna area mulut untuk masker (kalau sampler piksel diberikan).
function detectOcclusion(lm, ear, sampler) {
  const issues = [];
  let masked = false, sunglasses = false;

  // Kacamata hitam: EAR sangat rendah TAPI stabil (mata "tertutup" terus) sulit
  // dibedakan dari merem; kita tandai indikatif saja bila sampler tersedia.
  if (sampler) {
    // Sampel keseragaman warna area mulut → masker biasanya warna seragam.
    const mouthBox = boxFromPoints([lm[L.mouthLeft], lm[L.mouthRight], lm[L.mouthTop], lm[L.mouthBottom]], 1.6);
    const mouthVar = sampler(mouthBox);
    // Varian rendah juga dimiliki kulit mulus (mulut tertutup, cahaya rata), jadi masker hanya bila
    // area mulut BUKAN berwarna kulit. Kamera abu-abu/IR tidak lolos uji kulit: kembali ke perilaku lama.
    if (mouthVar != null && mouthVar < 0.012 && !(sampler.skinRatio?.(mouthBox) >= 0.5)) { masked = true; issues.push('masker'); }

    const eyeBox = boxFromPoints([lm[L.leyeOuter], lm[L.reyeOuter], lm[L.leyeTop], lm[L.reyeBottom]], 1.3);
    const eyeVar = sampler(eyeBox);
    if (eyeVar != null && eyeVar < 0.010) { sunglasses = true; issues.push('kacamata gelap'); }
  }

  return { masked, sunglasses, issues };
}

function boxFromPoints(pts, pad = 1) {
  let minX = 1, minY = 1, maxX = 0, maxY = 0;
  for (const p of pts) { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); }
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
  const hw = ((maxX - minX) / 2) * pad, hh = ((maxY - minY) / 2) * pad;
  return { x: clamp(cx - hw, 0, 1), y: clamp(cy - hh, 0, 1), w: clamp(hw * 2, 0, 1), h: clamp(hh * 2, 0, 1) };
}

// ============================================================================
// analyzeFaceMesh — fungsi utama. Input: array 478 landmark (punya .x/.y 0..1).
// opts.sampler(box) -> varian luminansi 0..1 untuk area (opsional, utk oklusi).
// ============================================================================
export function analyzeFaceMesh(landmarks, opts = {}) {
  if (!landmarks || landmarks.length < 468) return null;

  const pose = headPose(landmarks);
  const earL = eyeAspectRatio(landmarks, L.leyeTop, L.leyeBottom, L.leyeOuter, L.leyeInner);
  const earR = eyeAspectRatio(landmarks, L.reyeTop, L.reyeBottom, L.reyeOuter, L.reyeInner);
  const ear  = (earL + earR) / 2;
  const eyesOpen = ear > 0.20;

  // Ukuran wajah = tinggi (dahi→dagu) sebagai fraksi tinggi frame.
  const faceSize = clamp(landmarks[L.chin].y - landmarks[L.forehead].y, 0, 1);
  // Posisi pusat wajah terhadap tengah frame.
  const nose = landmarks[L.noseTip];
  const offCenter = Math.hypot(nose.x - 0.5, nose.y - 0.5);

  const occ = detectOcclusion(landmarks, ear, opts.sampler);

  // Skor kualitas 0..100 — penalti untuk pose, ukuran, posisi, mata, oklusi.
  let score = 100;
  const issues = [];
  if (faceSize < 0.18)            { score -= 35; issues.push('wajah terlalu kecil'); }
  else if (faceSize < 0.26)       { score -= 12; }
  if (Math.abs(pose.yaw) > 25)    { score -= 25; issues.push('wajah menengok'); }
  else if (Math.abs(pose.yaw) > 15) { score -= 10; }
  if (Math.abs(pose.pitch) > 22)  { score -= 18; issues.push('wajah terlalu naik/turun'); }
  if (Math.abs(pose.roll) > 20)   { score -= 10; issues.push('wajah miring'); }
  if (offCenter > 0.30)           { score -= 15; issues.push('wajah tidak di tengah'); }
  if (!eyesOpen)                  { score -= 20; issues.push('mata tertutup'); }
  if (occ.masked)                 { score -= 40; issues.push('wajah tertutup masker'); }
  if (occ.sunglasses)             { score -= 30; issues.push('kacamata gelap'); }
  score = clamp(Math.round(score), 0, 100);

  const poseClass = classifyPose(pose);

  return {
    pose,
    poseClass,
    ear: +ear.toFixed(3),
    eyesOpen,
    faceSize: +faceSize.toFixed(3),
    offCenter: +offCenter.toFixed(3),
    occlusion: occ,
    qualityScore: score,
    issues: [...new Set([...issues, ...occ.issues])],
    // Gate default untuk pre-filter: layak dikirim ke server / dijadikan enrollment.
    goodForRecognition: score >= 65 && faceSize >= 0.18 && eyesOpen && !occ.masked,
  };
}

// ============================================================================
// BlinkDetector — liveness sederhana. Suapi EAR tiap frame; kedip = EAR turun
// di bawah ambang lalu naik lagi. >=1 kedip dalam window => kemungkinan hidup.
// ============================================================================
export function createBlinkDetector({ closeAt = 0.16, openAt = 0.25, windowMs = 8000 } = {}) {
  let closed = false;
  let blinks = [];   // timestamp kedip
  return {
    update(ear, now) {
      if (!closed && ear < closeAt) { closed = true; }
      else if (closed && ear > openAt) { closed = false; blinks.push(now); }
      blinks = blinks.filter(t => now - t <= windowMs);
      return { blinks: blinks.length, live: blinks.length >= 1 };
    },
    reset() { closed = false; blinks = []; },
  };
}

// Sampler varian luminansi untuk sebuah box (0..1 koordinat) dari ImageData frame.
// Dipakai engine oklusi. Ringan: subsample piksel.
export function makeLumaVarianceSampler(imageData, frameW, frameH) {
  const { data, width } = imageData;
  // Iterasi ~64 piksel sampel di dalam box; cb(r, g, b) tiap piksel (0..255).
  const each = (box, cb) => {
    const x0 = Math.floor(box.x * frameW), y0 = Math.floor(box.y * frameH);
    const bw = Math.max(1, Math.floor(box.w * frameW)), bh = Math.max(1, Math.floor(box.h * frameH));
    const step = Math.max(1, Math.floor(Math.min(bw, bh) / 8));
    for (let y = y0; y < y0 + bh; y += step) {
      for (let x = x0; x < x0 + bw; x += step) {
        const i = (y * width + x) * 4;
        if (i < 0 || i + 2 >= data.length) continue;
        cb(data[i], data[i + 1], data[i + 2]);
      }
    }
  };
  const sampler = (box) => {
    let sum = 0, sum2 = 0, n = 0;
    each(box, (r, g, b) => { const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255; sum += luma; sum2 += luma * luma; n++; });
    if (n < 4) return null;
    const mean = sum / n;
    return Math.max(0, sum2 / n - mean * mean); // varian 0..~0.25
  };
  // Porsi piksel berwarna kulit di box: locus YCbCr (Chai & Ngan 1999) Cb 77..127, Cr 133..173.
  sampler.skinRatio = (box) => {
    let skin = 0, n = 0;
    each(box, (r, g, b) => {
      const cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b, cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;
      if (cb >= 77 && cb <= 127 && cr >= 133 && cr <= 173) skin++;
      n++;
    });
    return n < 4 ? null : skin / n;
  };
  return sampler;
}
